import { useFieldArray, useForm } from 'react-hook-form';
import { Plus, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { PollAPI } from '@shared/types/api';
import { useCreatePoll, useUpdatePoll } from '@/lib/api';
import { useToast } from '@/components/ux/Toast';
import { AdminDialog } from '../AdminDialog';
import { MarkdownTextarea } from '../MarkdownTextarea';
import { PollAnswerDetailsFields } from '../PollAnswerDetailsFields';
import { emptyPollAnswer, fromPollAnswerForm, toPollAnswerForm, type PollAnswerFormValue } from '../../utils/pollAnswers';

type PollStatus = PollAPI.Poll['status'];

type PollForm = {
  title: string;
  question: string;
  answers: PollAnswerFormValue[];
  publicResults: boolean;
  endsAt: string;
  status: PollStatus;
};

/** ISO/Date → value for a datetime-local input (local time). */
function toLocalInput(value: Date | string | null): string {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface Props {
  clubId: string;
  /** Poll to edit; omit to create a new club poll. */
  poll?: PollAPI.Poll;
  onClose: () => void;
}

/** Create or edit a club poll. Club polls are only visible to club members. */
export function ClubPollDialog({ clubId, poll, onClose }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const createPoll = useCreatePoll();
  const updatePoll = useUpdatePoll();

  const form = useForm<PollForm>({
    defaultValues: poll
      ? {
          title: poll.title,
          question: poll.question,
          answers: poll.answers.map(toPollAnswerForm),
          publicResults: poll.publicResults,
          endsAt: toLocalInput(poll.endsAt),
          status: poll.status,
        }
      : { title: '', question: '', answers: [emptyPollAnswer(), emptyPollAnswer()], publicResults: true, endsAt: '', status: 'active' },
  });
  const answers = useFieldArray({ control: form.control, name: 'answers' });

  const pending = createPoll.isPending || updatePoll.isPending;
  const done = (message: string) => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'clubs'] });
    toast.success(message);
    onClose();
  };
  const onError = (err: any) => toast.error(err?.message || 'Something went wrong');

  const save = form.handleSubmit((v) => {
    const payload = {
      title: v.title,
      question: v.question,
      answers: v.answers.filter((a) => a.text.trim()).map(fromPollAnswerForm),
      publicResults: v.publicResults,
      endsAt: v.endsAt ? new Date(v.endsAt).toISOString() : null,
    };
    if (payload.answers.length < 2) return toast.error('Add at least two options');

    if (poll) {
      updatePoll.mutate({ id: poll.id, data: { ...payload, status: v.status } }, { onSuccess: () => done('Poll updated'), onError });
      return;
    }
    // New polls are created as drafts; publish straight away unless kept as a draft.
    createPoll.mutate(
      { ...payload, answers: payload.answers.map(({ id: _id, ...a }) => a), clubId, targetCriteria: null },
      {
        onSuccess: (res) => {
          const id = res.data?.id;
          if (!id || v.status === 'draft') return done('Poll saved as draft');
          updatePoll.mutate({ id, data: { status: v.status } }, { onSuccess: () => done('Poll published to the club'), onError });
        },
        onError,
      },
    );
  });

  return (
    <AdminDialog
      open
      onClose={onClose}
      title={poll ? 'Edit poll' : 'New poll'}
      size="lg"
      footer={
        <>
          <select className="select select-bordered select-sm mr-auto" {...form.register('status')}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
            {poll && <option value="archived">Archived</option>}
          </select>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
          <button type="button" onClick={save} disabled={pending} className="btn btn-primary btn-sm">
            {poll ? 'Save' : 'Create poll'}
          </button>
        </>
      }
    >
      <form onSubmit={save} className="space-y-4 text-sm">
        <input className="input input-bordered w-full" placeholder="Title (e.g. Next month's book)" {...form.register('title', { required: true })} />
        <MarkdownTextarea
          registration={form.register('question', { required: true })}
          value={form.watch('question')}
          onChange={(v) => form.setValue('question', v, { shouldDirty: true })}
          placeholder="Which book should we read next?"
        />

        <div className="space-y-3">
          <div className="text-xs font-medium text-neutral-500">Options</div>
          {answers.fields.map((field, i) => (
            <div key={field.id} className="space-y-1">
              <div className="flex gap-2 items-center">
                <span className="w-5 text-xs text-neutral-500 text-right">{i + 1}</span>
                <input className="input input-bordered flex-1" placeholder={`Option ${i + 1}`} {...form.register(`answers.${i}.text`)} />
                {answers.fields.length > 2 && (
                  <button type="button" onClick={() => answers.remove(i)} className="text-neutral-500 hover:text-red-500" aria-label="Remove option">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <PollAnswerDetailsFields
                index={i}
                register={form.register}
                value={form.watch(`answers.${i}`)}
                onImageChange={(url) => form.setValue(`answers.${i}.imageUrl`, url, { shouldDirty: true })}
              />
            </div>
          ))}
          <button type="button" onClick={() => answers.append(emptyPollAnswer())} className="flex items-center gap-1 text-primary-500">
            <Plus className="w-4 h-4" /> Add option
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="flex items-center gap-2 dark:text-neutral-200">
            <input type="checkbox" className="checkbox checkbox-sm" {...form.register('publicResults')} /> Show results after voting
          </label>
          <label className="flex items-center gap-2 dark:text-neutral-200">
            <span className="text-neutral-500">Voting closes</span>
            <input type="datetime-local" className="input input-bordered input-sm" {...form.register('endsAt')} />
          </label>
        </div>
      </form>
    </AdminDialog>
  );
}
